import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Paper, 
  Typography, 
  Grid,
  Card, 
  CardContent, 
  CardActions, 
  Button,
  Chip,
  Box,
  LinearProgress
} from '@mui/material';
import { PayrollMonthService } from '../../services/payrollService';
import { PayrollMonth, PayrollStatus } from '../../types/payroll';
import { formatCurrency } from '../../utils/formatters';
import AddIcon from '@mui/icons-material/Add';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PeopleIcon from '@mui/icons-material/People';

const PayrollDashboard: React.FC = () => {
  const [payrollMonths, setPayrollMonths] = useState<PayrollMonth[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchPayrollMonths = async () => {
      try {
        setLoading(true);
        const response = await PayrollMonthService.getAllPayrollMonths();
        setPayrollMonths(response.data);
        setError(null);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load payroll months');
        console.error('Error fetching payroll months:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPayrollMonths();
  }, []);
  
  const handleCreatePayrollMonth = () => {
    navigate('/payroll/months/create');
  };
  
  const handleViewPayrollMonth = (id: number) => {
    navigate(`/payroll/months/${id}`);
  };
  
  const getStatusChipColor = (status: PayrollStatus) => {
    switch(status) {
      case 'DRAFT': return { color: 'default', label: 'Draft' };
      case 'SUBMITTED': return { color: 'primary', label: 'Submitted' };
      case 'APPROVED': return { color: 'success', label: 'Approved' };
      default: return { color: 'default', label: status };
    }
  };
  
  const renderPayrollMonthCards = () => {
    if (payrollMonths.length === 0) {
      return (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="textSecondary">
            No payroll months found. Create your first payroll month to get started.
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={handleCreatePayrollMonth}
            sx={{ mt: 2 }}
          >
            Create Payroll Month
          </Button>
        </Box>
      );
    }
    
    return (
      <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={3}>
        {payrollMonths.map((month) => {
          const statusConfig = getStatusChipColor(month.status);
          return (
            <Box gridColumn={{ xs: 'span 12', md: 'span 6', lg: 'span 4' }} key={month.id}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  transition: '0.3s',
                  '&:hover': {
                    boxShadow: 6,
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                      {new Date(month.period + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                    </Typography>
                    <Chip 
                      label={statusConfig.label} 
                      color={statusConfig.color as any} 
                      size="small" 
                    />
                  </Box>
                  
                  <Box display="flex" alignItems="center" mb={1}>
                    <CalendarTodayIcon fontSize="small" sx={{ mr: 1 }} />
                    <Typography variant="body2">
                      {month.working_days} working days
                    </Typography>
                  </Box>
                  
                  <Box display="flex" alignItems="center" mb={2}>
                    <PeopleIcon fontSize="small" sx={{ mr: 1 }} />
                    <Typography variant="body2">
                      {month.employee_count || 0} employees
                    </Typography>
                  </Box>
                  
                  <Box mb={1}>
                    <Typography variant="body2" color="textSecondary">
                      Created by: {month.created_by_name || 'Unknown'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Last updated: {new Date(month.updated_at || month.created_at || '').toLocaleDateString()}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    color="primary"
                    onClick={() => handleViewPayrollMonth(month.id)}
                  >
                    View Details
                  </Button>
                  {month.status === 'DRAFT' && (
                    <Button 
                      size="small" 
                      color="secondary"
                      onClick={() => navigate(`/payroll/months/${month.id}/edit`)}
                    >
                      Edit
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Box>
          );
        })}
      </Box>
    );
  };
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h1">
            Payroll Dashboard
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={handleCreatePayrollMonth}
          >
            Create Payroll Month
          </Button>
        </Box>
        
        {loading && <LinearProgress sx={{ mb: 3 }} />}
        
        {error && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
            <Typography>{error}</Typography>
          </Box>
        )}
        
        {renderPayrollMonthCards()}
      </Paper>
    </Container>
  );
};

export default PayrollDashboard;
